import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, Cog, Package, ShoppingBag, Bike, Truck, ArrowRight, ChevronRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api } from "../lib/api";
import MotorcycleCard from "../components/MotorcycleCard";
import BrandsSection from "../components/BrandsSection";
import GoogleReviews from "../components/GoogleReviews";

const HERO_IMG = "/hero-home.jpg";

const services = [
  { icon: Wrench, title: "Onderhoud", anchor: "onderhoud", text: "Periodiek onderhoud van alle merken, uitgevoerd volgens fabrieksvoorschriften." },
  { icon: Cog, title: "Herstellingen", anchor: "herstellingen", text: "Vakkundige herstellingen, klein of groot, snel en betrouwbaar." },
  { icon: Package, title: "Onderdelen", anchor: "onderdelen", text: "Origineel en aftermarket, snel geleverd en gemonteerd." },
  { icon: ShoppingBag, title: "Accessoires", anchor: "accessoires", text: "Handige accessoires en toebehoren voor je motor of scooter — bagage, beveiliging, onderhoud en styling." },
  { icon: Bike, title: "Verkoop", anchor: "verkoop", text: "Nieuwe en tweedehands motoren, geselecteerd op kwaliteit." },
  { icon: Truck, title: "Ophalen & leveren", anchor: "ophalen-leveren", text: "Wij halen je motor op en leveren ze terug — in heel België." },
];

export default function Home() {
  useSEO({
    title: "TT Motorsports Torhout | Motoren te koop, motoronderhoud & herstellingen",
    description: "TT Motorsports in Torhout: motoren te koop, tweedehands motoren, motoronderhoud, motor herstellingen, motoronderdelen en accessoires. Voor alle merken en alle types motoren — van crossmotor tot sport- en touringmotor.",
    keywords: "motoren te koop, tweedehands motoren, gebruikte motor kopen, motoronderhoud, motor onderhoud, motor herstelling, motor reparatie, motoronderdelen, motor accessoires, motor garage, motor specialist, TT Motorsports Torhout",
  });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/motorcycles", { params: { status: "beschikbaar", thumb: true, limit: 3 } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0 z-0">
          <picture className="block w-full h-full">
            <source media="(max-width: 768px)" srcSet="/hero-home-m.jpg" />
            <img src={HERO_IMG} alt="TT Motorsports werkplaats in Torhout — motoronderhoud en herstellingen voor alle merken en types motoren" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/85 to-[#0A0A0B]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-8 w-full pt-20 md:pt-24 pb-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <span className="w-10 h-[3px] bg-[#E10600]" />
              <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Changing the industry</span>
            </div>
            <h1 className="heading-xl text-[clamp(3rem,9vw,7.5rem)] text-white mb-4 md:mb-6">
              Passie voor<br /><span className="text-[#E10600]">motoren</span>
            </h1>
            <p className="text-base md:text-xl text-[#B8B9BE] max-w-xl mb-8 md:mb-10 leading-relaxed">
              Verkoop, onderhoud en herstelling voor <span className="text-white">alle merken en alle types motoren</span>. Van crossmotor tot sport- en touringmotor — professionele service voor iedere motorrijder.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link to="/motoren" data-testid="hero-cta-motoren"
                className="group bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-7 py-3.5 md:py-4 flex items-center justify-center gap-2 transition-colors w-full sm:w-auto">
                Bekijk tweedehands motoren <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/afspraak" data-testid="hero-cta-afspraak"
                className="border border-[#E10600] text-white hover:bg-[#E10600] font-display uppercase font-700 tracking-wide px-7 py-3.5 md:py-4 transition-colors w-full sm:w-auto text-center">
                Maak online een afspraak
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact"
                className="border border-[#2A2B31] hover:border-white text-white font-display uppercase font-700 tracking-wide px-7 py-3.5 md:py-4 transition-colors w-full sm:w-auto text-center">
                Contacteer ons
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <BrandsSection />

      {/* TWEEDEHANDS — automatisch 3 beschikbare motoren uit het aanbod */}
      {featured.length > 0 && (
        <section data-testid="home-used" className="section-pad bg-[#0D0D0F] border-y border-[#2A2B31]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="mb-8 md:mb-12">
              <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">In de kijker</span>
              <h2 data-testid="home-used-title" className="heading-xl text-4xl md:text-6xl text-white mt-3">Onze tweedehands motoren</h2>
            </div>
            <div data-testid="home-used-list"
              className="flex md:grid md:grid-cols-3 gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-5 px-5 md:mx-0 md:px-0 pb-2 md:pb-0">
              {featured.map((m) => (
                <div key={m.id} className="snap-start shrink-0 w-[85%] sm:w-[60%] md:w-auto">
                  <MotorcycleCard moto={m} cta="Bekijk motor" />
                </div>
              ))}
            </div>
            <div className="mt-8 md:mt-12 flex justify-center">
              <Link to="/motoren" data-testid="home-used-all"
                className="group w-full sm:w-auto bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-8 py-4 flex items-center justify-center gap-2 transition-colors">
                Bekijk alle tweedehands motoren <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section className="section-pad bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 mb-8 md:mb-14">
            <div>
              <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Wat we doen</span>
              <h2 className="heading-xl text-4xl md:text-6xl text-white mt-3">Onze diensten</h2>
            </div>
            <Link to="/diensten" className="font-display uppercase text-sm font-600 text-[#9A9CA3] hover:text-[#E10600] flex items-center gap-1 min-h-[44px] transition-colors">
              Alle diensten <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2B31] border border-[#2A2B31]">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`home-service-${i}`}
              >
                <Link
                  to={`/diensten#${s.anchor}`}
                  data-testid={`home-service-link-${s.anchor}`}
                  className="group block h-full bg-[#0A0A0B] hover:bg-[#141518] p-6 md:p-8 transition-colors"
                >
                  <s.icon size={32} className="text-[#E10600] mb-5" strokeWidth={1.5} />
                  <h3 className="font-display uppercase font-700 text-xl text-white mb-2">{s.title}</h3>
                  <p className="text-[#9A9CA3] text-sm leading-relaxed">{s.text}</p>
                  <span className="mt-5 inline-flex items-center gap-1 font-display uppercase text-xs font-700 tracking-wide text-[#9A9CA3] group-hover:text-[#E10600] transition-colors">
                    Meer info <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ALLE TYPES */}
      <section className="py-14 md:py-20 bg-[#0D0D0F] border-y border-[#2A2B31]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Voor iedereen</span>
            <h2 className="heading-xl text-3xl md:text-5xl text-white mt-3 mb-4">Alle merken. Alle types motoren.</h2>
            <p className="text-[#9A9CA3] text-lg leading-relaxed">
              TT Motorsports is niet enkel gespecialiseerd in cross- en enduromotoren. Wij bieden onderhoud, herstellingen en service voor <span className="text-white">zowel offroad als baanmotoren</span> — professionele service voor iedere motorrijder.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            {["Crossmotoren","Enduromotoren","Baanmotoren","Naked bikes","Sportmotoren","Touringmotoren","Adventuremotoren","Supermoto's","Scooters","Andere motorfietsen"].map((t, i) => (
              <motion.span key={t} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                data-testid={`type-chip-${i}`}
                className="font-display uppercase text-sm font-600 tracking-wide text-[#E6E6E8] border border-[#2A2B31] px-4 py-2 hover:border-[#E10600] hover:text-[#E10600] transition-colors">
                {t}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[#E10600]" />
        <div className="absolute inset-0 racing-stripe opacity-10" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="heading-xl text-4xl md:text-6xl text-white max-w-2xl">Klaar voor je volgende rit?</h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 shrink-0 w-full sm:w-auto">
            <Link to="/afspraak" data-testid="cta-banner-afspraak"
              className="bg-[#0A0A0B] hover:bg-black text-white font-display uppercase font-700 tracking-wide px-8 py-4 flex items-center justify-center gap-2 transition-colors">
              Maak online een afspraak <ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="cta-banner-contact"
              className="border border-white/50 hover:border-white text-white font-display uppercase font-700 tracking-wide px-8 py-4 text-center transition-colors">
              Contacteer ons
            </Link>
          </div>
        </div>
      </section>

      <GoogleReviews />
    </div>
  );
}
